import { z } from 'zod'
import { ISODate, Uuid, Version, blankAsAbsent, moneyField } from './primitives.ts'
import { PageQuery } from './pagination.ts'

/**
 * ══ HỢP ĐỒNG API — VỎ CONTAINER: ĐIỀU HÀNH (F29) ═════════════════════════════════════════════
 * Nguồn sự thật duy nhất cho `/api/v1/vo-cont/*`.
 *
 * ⚠ KHÔNG có ô TIỀN nào trong cả tệp, và đó là điều đáng nói: mảng vỏ ở F29 chỉ điều hành LỊCH và
 *   TÀI SẢN. Tiền của mảng này (dầu, sửa chữa, vật tư) là F30 — trộn vào đây là ép hai bài toán
 *   vào một khuôn rồi cả hai cùng chật.
 */

export const ContService = z.enum(['rent', 'move', 'repair'])
export const ContOrderStatus = z.enum(['draft', 'confirmed', 'running', 'done', 'cancelled'])
export const ContCatalogKind = z.enum(['place', 'cont_type', 'service_item', 'material', 'status_label'])

/** Sáu trạng thái điều hành của bản cũ (Tại ga · Đang vận hành · Đang lấy hàng · Điều vỏ · Khác · Sửa chữa). */
export const ContOpsStatus = z.enum(['at_station', 'operating', 'picking_up', 'empty_move', 'other', 'repairing'])

export const AddContainerBody = z.object({
  code: z.string().min(1, 'Chưa nhập số vỏ.'),
  size: blankAsAbsent(z.string()),
  contType: blankAsAbsent(z.string()),
  madeYear: z.number().int().min(1950).max(2100).optional(),
  ownership: z.enum(['own', 'leased']).default('own'),
  location: blankAsAbsent(z.string()),
  note: blankAsAbsent(z.string()),
})

/**
 * ⚠ CHỈ BA trạng thái đặt tay được. `on_order` KHÔNG có ở đây — nó SUY RA từ lịch đơn, và cho gõ
 *   tay là mở đường cho bảng vỏ nói khác với bảng đơn.
 */
export const ContainerStatusBody = z.object({
  version: Version,
  status: z.enum(['available', 'maintenance', 'retired']),
})

export const FleetQuery = z.object({ limit: PageQuery.shape.limit })

export const CreateContOrderBody = z.object({
  customerId: blankAsAbsent(Uuid),
  containerId: Uuid,
  service: ContService.default('rent'),
  fromDate: ISODate,
  /** Bỏ trống = chưa biết ngày trả. Luật chồng lịch coi là chiếm tới vô hạn. */
  toDate: blankAsAbsent(ISODate),
  fromPlace: blankAsAbsent(z.string()),
  toPlace: blankAsAbsent(z.string()),
  note: blankAsAbsent(z.string()),
})

export const RescheduleBody = z.object({
  version: Version,
  fromDate: ISODate,
  toDate: blankAsAbsent(ISODate),
})

export const ContOrderStatusBody = z.object({
  version: Version,
  status: ContOrderStatus,
})

export const ListContOrderQuery = PageQuery.extend({
  status: blankAsAbsent(ContOrderStatus),
})

export const AvailableQuery = z.object({
  fromDate: ISODate,
  toDate: blankAsAbsent(ISODate),
})

export const ContIdParams = z.object({ id: Uuid })

export const CatalogQuery = z.object({ kind: blankAsAbsent(ContCatalogKind) })

export const AddCatalogBody = z.object({
  kind: ContCatalogKind,
  code: z.string().min(1, 'Chưa nhập mã.'),
  name: z.string().min(1, 'Chưa nhập tên.'),
  sortOrder: z.number().int().min(0).max(9999).optional(),
  note: blankAsAbsent(z.string().max(500)),
  color: blankAsAbsent(z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Màu phải dạng #rrggbb.')),
})

export const UpdateCatalogBody = z.object({
  version: Version,
  name: z.string().optional(),
  note: z.string().max(500).optional(),
  color: z.string().regex(/^(#[0-9a-fA-F]{6})?$/, 'Màu phải dạng #rrggbb.').optional(),
})

export const UpdateContainerBody = z.object({
  version: Version,
  code: z.string().optional(),
  contType: z.string().optional(),
  note: z.string().optional(),
})

/** Đổi trạng thái điều hành. Luật "vận hành cần đơn · tại ga cần vị trí" ở miền (`opsStatusError`). */
export const ContainerOpsBody = z.object({
  version: Version,
  opsStatus: ContOpsStatus,
  locationId: blankAsAbsent(Uuid),
  jobId: blankAsAbsent(Uuid),
  note: z.string().optional(),
})

export const PluggedBody = z.object({ version: Version, plugged: z.boolean() })

/** Gán vỏ vào đơn lạnh tại bảng Điều hành vỏ lạnh — hai số phiên bản: của vỏ và của đơn. */
export const AssignReeferBody = z.object({
  containerId: Uuid,
  containerVersion: Version,
  jobVersion: Version,
})

export const ReeferConfirmBody = z.object({ version: Version })

export const EmptyBody = z.object({}).strict()

export type AddContainerBodyT = z.infer<typeof AddContainerBody>
export type ContainerStatusBodyT = z.infer<typeof ContainerStatusBody>
export type FleetQueryT = z.infer<typeof FleetQuery>
export type CreateContOrderBodyT = z.infer<typeof CreateContOrderBody>
export type RescheduleBodyT = z.infer<typeof RescheduleBody>
export type ContOrderStatusBodyT = z.infer<typeof ContOrderStatusBody>
export type ListContOrderQueryT = z.infer<typeof ListContOrderQuery>
export type AvailableQueryT = z.infer<typeof AvailableQuery>
export type CatalogQueryT = z.infer<typeof CatalogQuery>
export type AddCatalogBodyT = z.infer<typeof AddCatalogBody>
export type UpdateCatalogBodyT = z.infer<typeof UpdateCatalogBody>
export type UpdateContainerBodyT = z.infer<typeof UpdateContainerBody>
export type ContainerOpsBodyT = z.infer<typeof ContainerOpsBody>
export type PluggedBodyT = z.infer<typeof PluggedBody>
export type AssignReeferBodyT = z.infer<typeof AssignReeferBody>
export type ReeferConfirmBodyT = z.infer<typeof ReeferConfirmBody>

/* ══ DẦU · SỬA CHỮA · KHOÁ KỲ (F30) ══════════════════════════════════════════════════════════
 *
 * ⚠ LÍT LÀ `number`, TIỀN LÀ `Money`. Hai loại số khác nhau: lít có ba chữ số thập phân và làm
 *   tròn về đồng thì mất nửa lít mỗi phiếu; tiền thì đi bằng chuỗi thập phân như mọi nơi khác.
 */

/** Lít — dương, tối đa ba chữ số thập phân. Dùng cho lô nhập và phiếu xuất. */
const Litres = z.number().positive('Số lượng phải lớn hơn 0.').max(10_000_000)

export const ReceiveLotBody = z.object({
  recvDate: ISODate,
  vendorId: blankAsAbsent(Uuid),
  place: blankAsAbsent(z.string()),
  qtyIn: Litres,
  unitPrice: moneyField('Đơn giá'),
  invoiceNo: blankAsAbsent(z.string()),
  note: blankAsAbsent(z.string()),
})

export const FuelMoveKind = z.enum(['issue', 'transfer', 'adjust'])

/**
 * ⚠ `qty` Ở ĐÂY CHO PHÉP ÂM, vì phiếu ĐIỀU CHỈNH giảm là một số âm. Luật "xuất phải dương" nằm ở
 *   miền (`moveError`), nơi nó biết `kind` — lược đồ không phân biệt được hai ca đó.
 */
export const FuelMoveBody = z.object({
  moveDate: ISODate,
  kind: FuelMoveKind,
  containerId: blankAsAbsent(Uuid),
  /** Đơn lạnh mà phiếu xuất này đổ dầu cho — chỉ phiếu `issue` có vỏ (luật ở miền). */
  jobId: blankAsAbsent(Uuid),
  fromPlace: blankAsAbsent(z.string()),
  toPlace: blankAsAbsent(z.string()),
  qty: z.number().min(-10_000_000).max(10_000_000)
    .refine((n) => n !== 0, 'Số lượng phải khác 0.'),
  reason: blankAsAbsent(z.string()),
  note: blankAsAbsent(z.string()),
})

export const FuelStockQuery = z.object({ limit: PageQuery.shape.limit })
export const FuelMoveQuery = PageQuery

export const RepairClass = z.enum(['periodic', 'major'])
export const RepairStatus = z.enum([
  'draft', 'plan_review', 'plan_review_ops', 'plan_review_director', 'executing',
  'settle_review', 'settle_review_ops', 'settle_review_director', 'done', 'rejected', 'cancelled',
])

/**
 * Một HẠNG MỤC sửa chữa — chép `CoRepairItem` bản cũ. Báo giá tham khảo tối đa HAI nhà cung cấp.
 * ⚠ Tiền là chuỗi thập phân như mọi nơi; SỐ LƯỢNG là `number` (vật tư đo bằng mét, lít).
 */
export const RepairItemBody = z.object({
  id: z.string().min(1).max(64),
  name: z.string().max(300),
  desc: blankAsAbsent(z.string().max(1000)),
  vendorPlan: blankAsAbsent(z.string().max(300)),
  vendorActual: blankAsAbsent(z.string().max(300)),
  priority: blankAsAbsent(z.enum(['low', 'medium', 'high'])),
  costEst: blankAsAbsent(moneyField('Chi phí dự kiến')),
  quotes: z.array(z.object({
    vendorName: z.string().max(300),
    amount: moneyField('Báo giá'),
  })).max(2).optional(),
  qty: z.number().min(0).max(10_000_000).optional(),
  unitPrice: blankAsAbsent(moneyField('Đơn giá')),
  costSettle: blankAsAbsent(moneyField('Chi phí quyết toán')),
  doneDate: blankAsAbsent(ISODate),
  accepted: z.boolean().optional(),
  supplyMode: z.enum(['', 'to_stock', 'from_stock']).optional(),
  supplyId: blankAsAbsent(Uuid),
})

export const CreateRepairBody = z.object({
  containerId: Uuid,
  repClass: RepairClass.default('periodic'),
  description: z.string().min(1, 'Chưa nhập mô tả sự cố.'),
  garage: blankAsAbsent(z.string()),
  costEst: blankAsAbsent(moneyField('Chi phí dự kiến')),
  advanceAmount: blankAsAbsent(moneyField('Tạm ứng')),
  opsApprover: blankAsAbsent(z.string()),
  directorApprover: blankAsAbsent(z.string()),
})

/** Lưu chi tiết phiếu. `payDue: null` / `advanceAmount: null` là XOÁ; không gửi là không đổi. */
export const RepairDetailBody = z.object({
  version: Version,
  items: z.array(RepairItemBody).max(200),
  payDue: z.union([ISODate, z.literal(''), z.null()]).optional(),
  advanceAmount: z.union([moneyField('Tạm ứng'), z.null()]).optional(),
})

export const RepairVersionBody = z.object({ version: Version })

export const RepairRejectBody = z.object({
  version: Version,
  reason: blankAsAbsent(z.string().max(1000)),
})

export const SettleRepairBody = z.object({
  version: Version,
  items: z.array(RepairItemBody).max(200),
  settledDate: ISODate,
  payDue: z.union([ISODate, z.literal(''), z.null()]),
})

export const ListRepairQuery = PageQuery.extend({
  status: blankAsAbsent(RepairStatus),
})

export const CreateRelatedCostBody = z.object({
  description: z.string().min(1, 'Chưa nhập diễn giải.'),
  amount: moneyField('Số tiền'),
  containerIds: z.array(Uuid).min(1, 'Chọn ít nhất một vỏ áp dụng.').max(500),
  bankName: blankAsAbsent(z.string()),
  bankAccount: blankAsAbsent(z.string()),
  accountName: blankAsAbsent(z.string()),
})

export const RelatedCostQuery = z.object({ limit: PageQuery.shape.limit })

export const ContReportQuery = z.object({ from: ISODate, to: ISODate })

/** Khoá kỳ. `reason` không bắt buộc, nhưng khoá thì phải nói tới ngày nào. */
export const LockPeriodBody = z.object({
  lockedTo: ISODate,
  reason: blankAsAbsent(z.string()),
})

export type ReceiveLotBodyT = z.infer<typeof ReceiveLotBody>
export type FuelMoveBodyT = z.infer<typeof FuelMoveBody>
export type FuelStockQueryT = z.infer<typeof FuelStockQuery>
export type FuelMoveQueryT = z.infer<typeof FuelMoveQuery>
export type CreateRepairBodyT = z.infer<typeof CreateRepairBody>
export type RepairDetailBodyT = z.infer<typeof RepairDetailBody>
export type RepairVersionBodyT = z.infer<typeof RepairVersionBody>
export type RepairRejectBodyT = z.infer<typeof RepairRejectBody>
export type SettleRepairBodyT = z.infer<typeof SettleRepairBody>
export type ListRepairQueryT = z.infer<typeof ListRepairQuery>
export type CreateRelatedCostBodyT = z.infer<typeof CreateRelatedCostBody>
export type RelatedCostQueryT = z.infer<typeof RelatedCostQuery>
export type ContReportQueryT = z.infer<typeof ContReportQuery>
export type LockPeriodBodyT = z.infer<typeof LockPeriodBody>

/* ══ BÃI VỎ ▸ KHO VẬT TƯ · ĐƠN GIÁ CÔNG · SỬA TAY SỐ BÁO CÁO ══════════════════════════════════ */

export const CreateMaterialBody = z.object({
  name: z.string().min(1, 'Thiếu tên vật tư.'),
  unit: z.string().optional(),
  qty: z.coerce.number().min(0, 'Tồn vật tư không được âm.').optional(),
  unitPrice: blankAsAbsent(moneyField('Đơn giá vật tư')),
  buyDate: blankAsAbsent(ISODate),
  containerId: blankAsAbsent(Uuid),
  supplier: blankAsAbsent(z.string()),
  /** `qt` = nhập từ quyết toán sửa chữa · `mua` = mua ngoài. */
  source: z.enum(['qt', 'mua']).optional(),
  note: blankAsAbsent(z.string()),
})

export const UpdateMaterialBody = z.object({
  version: Version,
  name: z.string().optional(),
  unit: z.string().optional(),
  qty: z.coerce.number().min(0).optional(),
  qtyUsed: z.coerce.number().min(0).optional(),
  unitPrice: blankAsAbsent(moneyField('Đơn giá vật tư')),
  supplier: z.string().optional(),
  settled: z.boolean().optional(),
  note: z.string().optional(),
})

export const SetSalaryRateBody = z.object({ rate: moneyField('Đơn giá lương hiệu suất') })

/**
 * ⚠ BA Ô ĐỀU TUỲ CHỌN và "không gửi" nghĩa là KHÔNG ĐỔI: màn chỉ gửi ô người dùng vừa gõ, và hai
 *   ô kia phải nằm yên. Xem `upsertOverride`.
 */
export const SetReportOverrideBody = z.object({
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Kỳ phải có dạng YYYY-MM.'),
  /** Trống = số cả công ty · có = gõ đè RIÊNG một vỏ. */
  containerId: blankAsAbsent(Uuid),
  /** Ô cần XOÁ về "chưa gõ" — ở báo cáo theo vỏ, ô trống là "nhận phần chia đều". */
  clear: z.array(z.enum(['depreciation', 'otherCost', 'commonCost', 'interestCost'])).max(4).optional(),
  depreciation: blankAsAbsent(moneyField('Khấu hao')),
  otherCost: blankAsAbsent(moneyField('Chi phí khác')),
  commonCost: blankAsAbsent(moneyField('Chi phí chung')),
  /** Lãi vay vỏ lạnh của tháng (`0122`) — chỉ dòng cả công ty, nhập ở Dashboard ▸ Tổng quan. */
  interestCost: blankAsAbsent(moneyField('Chi phí lãi vay')),
  note: blankAsAbsent(z.string()),
})

export const MaterialIdParams = z.object({ id: Uuid })

/* ══ BÁO CÁO LỜI LỖ VỎ LẠNH ═══════════════════════════════════════════════════════════════════ */

export const RevenueBasis = z.enum(['confirmed', 'delivered', 'invoiced'])

export const ProfitReportQuery = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
  basis: RevenueBasis.default('confirmed'),
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Tháng phải có dạng YYYY-MM.'),
})

export const ReeferJobQuery = z.object({ limit: PageQuery.shape.limit })

export type ProfitReportQueryT = z.infer<typeof ProfitReportQuery>
export type ReeferJobQueryT = z.infer<typeof ReeferJobQuery>

export type CreateMaterialBodyT = z.infer<typeof CreateMaterialBody>
export type UpdateMaterialBodyT = z.infer<typeof UpdateMaterialBody>
export type SetSalaryRateBodyT = z.infer<typeof SetSalaryRateBody>
export type SetReportOverrideBodyT = z.infer<typeof SetReportOverrideBody>

/* ══ 💸 CHI PHÍ — NHẬT KÝ DẦU THEO VỎ · SAN DẦU · TỒN DẦU · XÁC NHẬN PHÂN BỔ ═════════════════════
 * Chép `container-ops/Fuel.tsx`. Lít là `number`, tiền là `Money` — như phần F30 ở trên.
 */

const FuelVatRate = z.union([z.literal(0), z.literal(5), z.literal(8), z.literal(10)])

export const FuelLogBody = z.object({
  containerId: Uuid,
  jobId: blankAsAbsent(Uuid),
  logDate: ISODate,
  liters: Litres,
  cost: moneyField('Thành tiền'),
  station: blankAsAbsent(z.string().max(200)),
  staff: blankAsAbsent(z.string().max(200)),
  vendorId: blankAsAbsent(Uuid),
  payeeAccount: blankAsAbsent(z.string().max(100)),
  payeeBank: blankAsAbsent(z.string().max(200)),
  payeeHolder: blankAsAbsent(z.string().max(200)),
  expenseItem: blankAsAbsent(z.string().max(50)),
  mgmtOnly: z.boolean().optional(),
  hasInvoice: z.boolean().optional(),
  invoiceNo: blankAsAbsent(z.string().max(100)),
  invoiceDate: blankAsAbsent(ISODate),
  vatRate: blankAsAbsent(FuelVatRate),
  acctExpense: blankAsAbsent(z.string().max(20)),
  acctPayable: blankAsAbsent(z.string().max(20)),
  acctVat: blankAsAbsent(z.string().max(20)),
})

export const EditFuelLogBody = FuelLogBody.extend({ version: Version })

export const FuelBankNoteBody = z.object({
  version: Version,
  accountId: Uuid,
  txnDate: ISODate,
  amount: moneyField('Số tiền'),
  description: z.string().min(1, 'Chưa nhập nội dung chuyển khoản.').max(500),
  mgmtOnly: z.boolean(),
})

export const FuelTransferBody = z.object({
  fromId: Uuid,
  toId: Uuid,
  liters: Litres,
  reason: blankAsAbsent(z.string().max(500)),
  transferDate: blankAsAbsent(ISODate),
})

export const EditFuelTransferBody = FuelTransferBody.extend({ version: Version })

export const DecideFuelTransferBody = z.object({ version: Version, approve: z.boolean() })

const MonthKey = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Tháng phải có dạng YYYY-MM.')

export const FuelLogStockQuery = z.object({ month: MonthKey })
export const FuelYearQuery = z.object({ year: z.coerce.number().int().min(2000).max(2100) })
export const FuelByJobQuery = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
  month: z.coerce.number().int().min(0).max(12),
})

export const FuelStockAdjBody = z.object({
  containerId: Uuid,
  month: MonthKey,
  field: z.enum(['realL', 'openOverrideVal', 'adjCl']),
  value: z.number().min(-1e15).max(1e15).nullable(),
})

export const ConfirmAllocBody = z.object({
  kind: z.enum(['fuel', 'depreciation']),
  picks: z.array(z.object({ jobId: Uuid, version: Version })).min(1, 'Chưa chọn đơn nào.').max(500),
})

export const FuelListQuery = z.object({ limit: PageQuery.shape.limit })

export type FuelLogBodyT = z.infer<typeof FuelLogBody>
export type EditFuelLogBodyT = z.infer<typeof EditFuelLogBody>
export type FuelBankNoteBodyT = z.infer<typeof FuelBankNoteBody>
export type FuelTransferBodyT = z.infer<typeof FuelTransferBody>
export type EditFuelTransferBodyT = z.infer<typeof EditFuelTransferBody>
export type DecideFuelTransferBodyT = z.infer<typeof DecideFuelTransferBody>
export type FuelLogStockQueryT = z.infer<typeof FuelLogStockQuery>
export type FuelYearQueryT = z.infer<typeof FuelYearQuery>
export type FuelByJobQueryT = z.infer<typeof FuelByJobQuery>
export type FuelStockAdjBodyT = z.infer<typeof FuelStockAdjBody>
export type ConfirmAllocBodyT = z.infer<typeof ConfirmAllocBody>
export type FuelListQueryT = z.infer<typeof FuelListQuery>
